import { baseApi } from './baseApi';
import { ServiceTypes } from '../constants';
import { isAbortError } from '../helpers/apiErrors';
import { addNotification } from '../store';

const mapApiToStore = (item: IGroupFromApi): IMappedGroup => ({
  id: item.ID,
  name: item.Name,
  updatedAt: item.UpdatedAt,
  createdAt: item.CreatedAt,
  deletedAt: item.DeletedAt,
});

export const groupsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGroups: builder.query<IMappedGroup[], void>({
      query: () => `/groups`,
      transformResponse: (result: any) => result.data.map(mapApiToStore),
      providesTags: () => [{ type: ServiceTypes.GROUPS, id: 'LIST' }],
    }),
    getGroupsByMe: builder.query<IMappedGroup[], void>({
      query: () => `/groups/me`,
      transformResponse: (result: any) => result.data.map(mapApiToStore),
      providesTags: () => [{ type: ServiceTypes.GROUPS, id: 'LIST' }],
    }),
    getGroupById: builder.query<IMappedGroup, { groupId: number }>({
      query: ({ groupId }) => `/groups/${groupId}`,
      transformResponse: (result: any) => mapApiToStore(result.data),
      providesTags: (result, error, { groupId }) => [{ type: ServiceTypes.GROUPS, id: groupId }],
    }),
    getGroupUsers: builder.query<IGroupUsersFromApi[], { groupId: number }>({
      query: ({ groupId }) => `/groups/${groupId}/users`,
      transformResponse: (result: INgApiResponse<IGroupUsersFromApi[]>) => result.data,
      providesTags: (result, error, { groupId }) => [{ type: ServiceTypes.GROUPS, id: `${groupId}_USERS` }],
    }),
    createGroup: builder.mutation<void, { data: Partial<IGroupFromApi> }>({
      query: ({ data }) => ({
        url: `/groups`,
        method: 'POST',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const groupName = arg.data.Name;

        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              type: 'success',
              message: `Group "${groupName}" has been successfully created!`,
            }),
          );
        } catch (error) {
          if (isAbortError(error)) return;
          const message = `Failed to create group "${groupName}"!`;
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
      invalidatesTags: () => [{ type: ServiceTypes.GROUPS, id: 'LIST' }],
    }),
    updateGroup: builder.mutation<void, { groupId: number; data: Partial<IGroupFromApi> }>({
      query: ({ data, groupId }) => ({
        url: `/groups/${groupId}`,
        method: 'PUT',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const groupName = arg.data.Name;

        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              type: 'success',
              message: `Group  "${groupName}" has been successfully updated!`,
            }),
          );
        } catch (error) {
          if (isAbortError(error)) return;
          const message = `Failed to update group "${groupName}"!`;
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
      invalidatesTags: (result, error, { groupId }) => [
        { type: ServiceTypes.GROUPS, id: 'LIST' },
        { type: ServiceTypes.GROUPS, id: groupId },
      ],
    }),
    deleteGroup: builder.mutation<void, { groupId: number; groupName: string }>({
      query: ({ groupId }) => ({
        url: `/groups/${groupId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const { groupName } = arg;

        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              type: 'success',
              message: `Group "${groupName}" has been successfully deleted!`,
            }),
          );
        } catch (error) {
          if (isAbortError(error)) return;
          const message = `Failed to delete group "${groupName}"!`;
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
      invalidatesTags: (result, error, { groupId }) => [
        { type: ServiceTypes.GROUPS, id: 'LIST' },
        { type: ServiceTypes.GROUPS, id: groupId },
      ],
    }),
    detachUserFromGroup: builder.mutation<void, { groupId: number; userId: number }>({
      query: ({ groupId, userId }) => ({
        url: `/groups/${groupId}/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: ServiceTypes.GROUPS, id: `${groupId}_USERS` }],
    }),
    attachUserToGroup: builder.mutation<void, { groupId: number; userId: number }>({
      query: ({ groupId, userId }) => ({
        url: `/groups/${groupId}/users`,
        method: 'POST',
        body: {
          user_id: userId,
        },
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: ServiceTypes.GROUPS, id: `${groupId}_USERS` }],
    }),
    getGroupGrants: builder.query<IGroupResourceGrant[], { groupId: number }>({
      query: ({ groupId }) => `/groups/${groupId}/grants`,
      transformResponse: (result: INgApiResponse<IGroupResourceGrant[]>) => result.data,
      providesTags: (result, error, { groupId }) => [{ type: ServiceTypes.GROUPS, id: `${groupId}_GRANTS` }],
    }),
  }),
});

export const {
  useGetGroupsQuery,
  useGetGroupByIdQuery,
  useGetGroupsByMeQuery,
  useGetGroupUsersQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useDetachUserFromGroupMutation,
  useAttachUserToGroupMutation,
  useGetGroupGrantsQuery,
} = groupsService;
